import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import DataTable, { Column } from "@/components/layouts/DataTable";
import DateFilterButton from "@/components/layouts/DateFilterButton";
import DeleteConfirmationDialog from "@/components/layouts/DeleteConfirmationDialog";
import PageHeader from "@/components/layouts/PageHeader";
import SearchInput from "@/components/layouts/SearchInput";
import PageLoader from "@/components/loader/PageLoader";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faEdit,
  faSearch,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface OptionListItem {
  id: number;
  name: string;
  created_at: string;
}

interface SortState {
  field: keyof OptionListItem;
  direction: "asc" | "desc";
}

const OptionList = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [optionListItems, setOptionListItems] = useState<OptionListItem[]>([]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);

  const [sortState, setSortState] = useState<SortState>({
    field: "name",
    direction: "asc",
  });

  const columns: Column<OptionListItem>[] = [
    {
      key: "name",
      header: t("back.management.menu.optionList.table.name"),
      sortable: true,
      bold: true,
    },
    {
      key: "created_at",
      header: t("back.management.menu.optionList.table.date"),
      sortable: true,
    },
    {
      key: "action",
      header: t("back.management.menu.optionList.table.action"),
    },
  ];

  const downloadCSV = () => {
    const csvRows = [
      ["ID", "Item Name", "Date"],

      ...optionListItems.map((item) => [item.id, item.name, item.created_at]),
    ];

    const csvData = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", "optionList.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleFilterStartDateChange = (date: Date | null) => {
    setFilterStartDate(date);
  };

  const handleFilterEndDateChange = (date: Date | null) => {
    setFilterEndDate(date);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const openDeleteConfirmation = (id: number) => {
    setItemToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setItemToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        if (!loading && axiosInstance) {
          const apiUrl =
            import.meta.env.VITE_APP_REDBIRDPOSBE_OPTIONLIST_INFORMATION ?? "";
          await axiosInstance.delete(`${apiUrl}${itemToDelete}`);
          fetchOptionListItems();
          toast.success(t("back.management.menu.optionList.successdelete"));
        }
      } catch (error) {
        console.error("Error deleting menu item:", error);
        toast.error(t("back.management.menu.optionList.faileddelete"));
      } finally {
        closeDeleteConfirmation();
      }
    }
  };

  const fetchOptionListItems = async () => {
    try {
      if (!loading && axiosInstance && user?.selectedBranch) {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_OPTIONLIST_INFORMATION ?? "";
        let url = `${apiUrl}?branch=${user.selectedBranch.id}&page=${currentPage}&page_size=${itemsPerPage}`;
        if (searchQuery) {
          url += `&name=${encodeURIComponent(searchQuery)}`;
        }
        if (filterStartDate) {
          const formattedStartDate = filterStartDate.toISOString();
          url += `&created_at__gt=${formattedStartDate}`;
        }
        if (filterEndDate) {
          const formattedEndDate = filterEndDate.toISOString();
          url += `&created_at__lt=${formattedEndDate}`;
        }
        const response = await axiosInstance.get(url);
        const fetchedItems = response.data.results.map(
          (item: OptionListItem) => {
            // Convert and format the 'created_at' date
            const zonedDate = toZonedTime(item.created_at, "America/Chicago");
            const formattedDate = format(zonedDate, "MM/dd/yyyy, HH:mm");
            return { ...item, created_at: formattedDate };
          },
        );

        // Sorting logic
        fetchedItems.sort((a: OptionListItem, b: OptionListItem) => {
          if (sortState.field === "created_at") {
            // Convert back to dates for comparison
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortState.direction === "asc"
              ? dateA.getTime() - dateB.getTime()
              : dateB.getTime() - dateA.getTime();
          } else {
            const aValue = a[sortState.field];
            const bValue = b[sortState.field];
            if (typeof aValue === "number" && typeof bValue === "number") {
              return sortState.direction === "asc"
                ? aValue - bValue
                : bValue - aValue;
            } else if (
              typeof aValue === "string" &&
              typeof bValue === "string"
            ) {
              return sortState.direction === "asc"
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
            }
          }
          return 0;
        });

        setOptionListItems(fetchedItems);
        setTotalItems(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching optionList items:", error);
    }
  };

  const handleSortChange = (field: keyof OptionListItem) => {
    setSortState((prevState) => ({
      field,
      direction:
        prevState.field === field && prevState.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Immediate initial fetch on component mount
  useEffect(() => {
    // Only run this on initial mount when we have the required dependencies
    if (axiosInstance !== null && axiosInstance !== undefined && user?.selectedBranch) {
      fetchOptionListItems();
    }
  }, [axiosInstance, user?.selectedBranch]); 

  // Debounced fetch only for search changes
  useEffect(() => {
    // Skip for initial load
    if (searchQuery === '' && currentPage === 1) {
      return;
    }
    
    const timerId = setTimeout(() => {
      fetchOptionListItems();
    }, 1000);

    return () => clearTimeout(timerId);
  }, [searchQuery]); 

  // Non-debounced updates for everything else
  useEffect(() => {
    // Skip the initial render
    if (currentPage === 1 && sortState.field === "name" && sortState.direction === "asc") {
      return;
    }
    fetchOptionListItems();
  }, [currentPage, sortState, filterStartDate, filterEndDate]); 

  const renderMobileCard = (item: OptionListItem) => (
    <div className="flex w-full items-center justify-between">
      <div>
        <p className="font-bold text-gray-900">{item.name}</p>
        <p className="text-gray-700">{item.created_at}</p>
      </div>
      <div className="flex h-full flex-col justify-between">
        <Link
          to={`/menu/option-lists/edit/${item.id}`}
          className="fa-2x mb-2 text-gray-600 hover:text-gray-800"
        >
          <FontAwesomeIcon icon={faEdit} />
        </Link>
        <button
          onClick={() => openDeleteConfirmation(item.id)}
          className="fa-2x text-red-600 hover:text-red-800"
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </button>
      </div>
    </div>
  );

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <div className="container mx-auto px-4 py-2">
          <PageHeader
            title={t("back.management.menu.optionList.title")}
            exportLabel={t("back.management.menu.optionList.export")}
            addLabel={t("back.management.menu.optionList.addOptionList")}
            onExport={downloadCSV}
            onAdd={() => navigate("/menu/option-lists/add")}
          />

          <DeleteConfirmationDialog
            isOpen={deleteConfirmationOpen}
            onClose={closeDeleteConfirmation}
            onConfirm={confirmDelete}
            title={t(
              "back.management.menu.optionList.deleteConfirmation.title",
            )}
            message={t(
              "back.management.menu.optionList.deleteConfirmation.message",
            )}
          />

          <div className="mb-4 flex flex-col justify-between space-y-2 lg:flex-row lg:items-center lg:space-y-0">
            <div className="relative mr-4 max-w-md flex-1">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
              />
              <SearchInput
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t(
                  "back.management.menu.optionList.searchOptionList",
                )}
              />
            </div>
            <div className="relative mr-36">
              <DateFilterButton
                onStartDateChange={handleFilterStartDateChange}
                onEndDateChange={handleFilterEndDateChange}
                startDate={filterStartDate}
                endDate={filterEndDate}
                labelText={t("back.management.menu.optionList.selectDate")}
              />
            </div>
          </div>

          <DataTable
            data={optionListItems}
            columns={columns}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalItems={totalItems}
            sortState={sortState}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            onEdit={(id) => navigate(`/menu/option-lists/edit/${id}`)}
            onDelete={openDeleteConfirmation}
            getStatusColor={() => ""}
            renderMobileCard={renderMobileCard}
          />
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
};

export default OptionList;
